import { css } from '@emotion/react'
import styled from '@emotion/styled'
import PreviousIcon from '@mui/icons-material/ChevronLeft'
import NextIcon from '@mui/icons-material/ChevronRight'
import Container from '@mui/material/Container'
import { Fragment, memo, useCallback, useEffect, useRef } from 'react'
import { getNextOrPreviousModel } from '../../../../actions/getNextOrPreviousModel'
import { openMenuDialog } from '../../../../actions/menu'
import { openMessage } from '../../../../actions/message'
import { openProfile } from '../../../../actions/nav'
import { openRoom, pinRoom, setLoungeSurrogate } from '../../../../actions/room'
import {
    addFriend,
    bookmarkModel,
    hideModel,
    ignoreUser,
    modelGetSocialMedia,
    removeBookmark,
    removeFriend,
} from '../../../../actions/user'
import { setMenuIDState } from '../../../../app-state/state.menu'
import { MENU } from '../../../../constants/dialogs'
import { AppStateType, VideoType } from '../../../../constants/enums'
import { QUICK_REPLY } from '../../../../constants/floaters'
import { isLounge } from '../../../../mfcLogic/userProperties/user'
import { MODALSTATE } from '../../../../state/modals'
import { openHideModelConfirmation } from '../../../../state/modals/state.confirmation'
import {
    closeDialog,
    openWhiteboardDialog,
} from '../../../../state/modals/state.dialog'
import {
    openAuthenticationDrawer,
    openReportUserDrawer,
    openTipsDrawer,
} from '../../../../state/modals/state.drawer'
import {
    minimizeFloater,
    openQuickReply,
} from '../../../../state/modals/state.floater'
import { KeyEvent, registerKeyHandler } from '../../../../state/state.keys'
import { AppRouter } from '../../../../state/state.router'
import { slugToID } from '../../../../state/state.users'
import i18n from '../../../../utilities/i18n'
import { safeObj } from '../../../../utilities/safe'
import {
    TransitionTypes,
    motionTransition,
} from '../../../../utilities/transitions'
import IconButton from '../../../Buttons/IconButton'
import { useAppStateSelector } from '../../../Hooks/useAppState'
import { useDialogSelector, useFloatingRoomId } from '../../../Hooks/useModal'
import { useModelColor } from '../../../Hooks/useModelColor'
import { usePMSelector } from '../../../Hooks/usePM'
import { useIsRoomPinned } from '../../../Hooks/useRoom'
import { useScreenStateSelector } from '../../../Hooks/useScreenState'
import { useUserSelector } from '../../../Hooks/useUser'
import Calendar from '../../../Icons/Calendar'
import MapMarker from '../../../Icons/MapMarker'
import { SocialIcon, SocialPlatforms } from '../../../Icons/SocialIcon'
import BadgeAvatar from '../../../common/BadgeAvatar'
import FlexGrid from '../../../common/FlexGrid'
import Name from '../../../common/Name'
import NameAvatar from '../../../common/NameAvatar'
import Share from '../../../common/Share'
import { getThemeSpacing } from '../../../styles/appTheme'
import APP_BREAKPOINTS, { minWindowWidth } from '../../../styles/breakpoints'
import { palette } from '../../../styles/colors'
import CounterViewers from '../../CounterViewers'
import Empty from '../../Empty'
import Actions from '../../Profile/Actions'
import Header from '../../Profile/Header'
import Tags from '../../Tags'
import Video from '../../Video'
import YouMayLike from '../../YouMayLike/embedded/EmbeddedRecommendations'

const StyledRoot = styled('div')({
    flexDirection: 'column',
    borderTopRightRadius: 4,
    borderTopLeftRadius: 4,
    position: 'relative',
    overflow: 'hidden',
    overflowX: 'auto',
    display: 'flex',
    height: '100%',
    width: '100%',
})
const StyledVideoContainer = styled('div')({
    width: '100%',
    display: 'block',
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
})
const StyledBody = styled('div')({
    msOverflowStyle: 'none',
    padding: getThemeSpacing(),
    overflowY: 'auto',
    height: '100%',
})
const StyledIconsContainer = styled('div')({
    justifyContent: 'center',
    display: 'flex',
    flexShrink: 0,
})
const StyledSocialIconWrapper = styled('div')({
    margin: getThemeSpacing(),
})
const previousButtonStyles = css({
    boxShadow: '0px 0px 3px 1px ' + palette.dialogButtonShadows,
    position: 'absolute',
    background: palette.background,
    top: '57%',
    '&:hover': {
        background: palette.background,
    },
    left: -31,
})
const nextButtonStyles = css({
    right: -31,
})
const navButtonStyles = css({
    boxShadow: '0px 0px 3px 1px ' + palette.dialogButtonShadows,
    position: 'absolute',
    background: palette.background,
    top: '57%',
    '&:hover': {
        background: palette.background,
    },
})

const avatarStyles = css({
    height: 55,
    maxWidth: 70,
})
const tagsStyles = css({
    padding: getThemeSpacing(1.5),
    paddingTop: getThemeSpacing(1),
    marginTop: 0,
})
const topicGridStyles = css({
    wordBreak: 'break-all',
    fontSize: 12,
    minHeight: 24,
})
const streamStyles = css({
    position: 'relative',
    background: '#000',
    flexShrink: 0,
    flexGrow: 0,
})
const memberMenuStyles = css({
    marginBottom: getThemeSpacing(3),
})
const shareLogoStyles = css({
    position: 'absolute',
    right: 0,
    top: 0,
    padding: 5,
    background: 'transparent',
})
const rankStyles = css({
    fontSize: 12,
    fontWeight: 500,
    textAlign: 'center',
    paddingBottom: getThemeSpacing(),
    justifyContent: 'center',
    [minWindowWidth(APP_BREAKPOINTS.sm)]: {
        paddingBottom: getThemeSpacing(1.5),
    },
})

const counterViewerStyles = css({
    fontSize: '11px',
})

const StyledModelDetailsRow = styled('div')({
    display: 'flex',
    flexFlow: 'column nowrap',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    width: '100%',
    'div:not(:last-child)': {
        paddingRight: getThemeSpacing(0.5),
    },
    fontSize: '11px',
})

const StyledRank = styled('span')({
    paddingRight: getThemeSpacing(1),
})

const StyledModelDetailElement = styled('div')({
    lineHeight: 1,
    display: 'flex',
    fontSize: '11px',
    fontWeight: 500,
    padding: 0,
    background: palette.background,
    alignItems: 'center',
    '& svg': {
        marginRight: getThemeSpacing(0.375),
    },
})

const StyledNameTopicGrid = styled(FlexGrid)({
    marginTop: '-2px',
})

const StyledMenuHeaderGrid = styled('div')({
    display: 'grid',
    gridTemplateColumns: '52px calc(100% - 52px)',
    paddingTop: getThemeSpacing(0.5),
    paddingBottom: getThemeSpacing(),
    position: 'relative',
    [minWindowWidth(APP_BREAKPOINTS.sm)]: {
        gridTemplateColumns: '68px calc(100% - 68px)',
    },
})

const StyledCountryLabel = styled('div')({
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    whiteSpace: 'pre',
})

const onCloseDialog = () => {
    const id = slugToID[MODALSTATE.dialog.state.slug]
    motionTransition(() => closeDialog(true), {
        id,
        transitionSource: TransitionTypes.MENU,
        transitionDestination: AppRouter.route.lastTransitionSource,
    })
}

export interface MenuProps {
    hideNextPrev?: boolean
    message?: boolean
    room?: boolean
    slug: string
    noVideo?: boolean
    lounge?: boolean
}

const Menu = memo((props: MenuProps) => {
    const isAuthenticated = useAppStateSelector(
        AppStateType.APP,
        'authenticated',
    )
    const isMobile = useAppStateSelector(AppStateType.APP, 'isMobile')

    const dialogID = useDialogSelector('type')
    const menuOpen = dialogID === MENU
    const { slug, hideNextPrev, room, message } = safeObj(props)
    const roomID = useFloatingRoomId()
    const isActiveRoomLounge = isLounge(roomID)
    const activeMessageID = usePMSelector('activeID')
    const alreadyClicked = useRef(false)
    const scrollRef = useRef<HTMLDivElement>(null)
    const id = useUserSelector(slug, 'id', { lookup: true })
    const recordings = useUserSelector(slug, 'shareRecordings') ?? 0
    const albums = useUserSelector(slug, 'shareAlbums') ?? 0
    const isBanned = useUserSelector(slug, 'banned')
    const isBroadcasting = useUserSelector(slug, 'isBroadcasting')
    const isTruePrivate = useUserSelector(slug, 'isTruePrivate')
    const isPrivateShow = useUserSelector(slug, 'isPrivateShow')
    const hideCamscore = useUserSelector(slug, 'hideCamscore')
    const isGroupShow = useUserSelector(slug, 'isGroupShow')
    const streamType = useUserSelector(slug, 'streamType')
    const instagram = useUserSelector(slug, 'instagram')
    const isClubShow = useUserSelector(slug, 'isClubShow')
    const isBookmark = useUserSelector(slug, 'isBookmark')
    const isOffline = useUserSelector(slug, 'isOffline')
    const isFriend = useUserSelector(slug, 'isFriend')
    const isHidden = useUserSelector(slug, 'isHidden')
    const isKicked = useUserSelector(slug, 'isKicked')
    const isIgnored = useUserSelector(slug, 'isIgnored')
    const hasNote = useUserSelector(slug, 'hasNote')
    const hasWhiteboard = useUserSelector(slug, 'hasWhiteboard')
    const isPublicShow = useUserSelector(slug, 'isPublicShow')
    const isModel = useUserSelector(slug, 'isModel')
    const viewers = useUserSelector(slug, 'viewers')
    const twitter = useUserSelector(slug, 'twitter')
    const country = useUserSelector(slug, 'country')
    const isAway = useUserSelector(slug, 'isAway')
    const is16x9 = useUserSelector(slug, 'is16x9')
    const banned = useUserSelector(slug, 'banned')
    const topic = useUserSelector(slug, 'topic')
    const score = useUserSelector(slug, 'score')
    const color = useUserSelector(slug, 'color')
    const name = useUserSelector(slug, 'name')
    const tags = useUserSelector(slug, 'tags')
    const rank = useUserSelector(slug, 'rank')
    const age = useUserSelector(slug, 'age')

    const windowWidth = useScreenStateSelector('windowWidth')
    const pinLimitReached = useScreenStateSelector('pinLimitReached')
    const singlePin = useScreenStateSelector('singlePin')

    const modelColor = useModelColor(color)
    const hasShare = albums > 0 || recordings > 0
    const isRoomPinned = useIsRoomPinned(id)
    const enableBrowsing = !message && !room && !hideNextPrev
    const inRoomOrMessage = Boolean(room || message)
    const sameModel = id === activeMessageID || id === roomID
    const inShow = streamType !== 'public'
    const hiddenShow =
        (isTruePrivate ||
            isPrivateShow ||
            isGroupShow ||
            isClubShow ||
            isAway) &&
        !inShow

    const showScorePanel =
        !isOffline && (rank > 0 || Boolean(!hideCamscore && score))

    // listen to screen resolution due to how we handle Avatar
    const isSmallResolution = windowWidth <= APP_BREAKPOINTS.sm

    const inHiddenShow =
        isTruePrivate || isPrivateShow || isGroupShow || isClubShow
    const noShowType = isOffline || inHiddenShow || isAway || !isBroadcasting
    const isViewable = !noShowType && !isBanned && !isKicked
    const showSinglePin = singlePin && isViewable
    const canPin =
        isRoomPinned ||
        ((!pinLimitReached || showSinglePin) &&
            isViewable &&
            !(isMobile && (room || message)))

    useEffect(() => {
        setMenuIDState(id)
    }, [id])

    useEffect(() => {
        if (!enableBrowsing) return
        return registerKeyHandler(
            KeyEvent.Keydown,
            () => {
                const previous = getNextOrPreviousModel(slug, {
                    previous: true,
                    returnSlug: 1,
                })
                openMenuDialog(previous)
            },
            ['ArrowLeft'],
            { exclusive: true },
        )
    }, [enableBrowsing, slug])

    useEffect(() => {
        if (!enableBrowsing) return
        return registerKeyHandler(
            KeyEvent.Keydown,
            () => {
                const next = getNextOrPreviousModel(slug, {
                    returnSlug: 1,
                })
                openMenuDialog(next)
            },
            ['ArrowRight'],
            { exclusive: true },
        )
    }, [enableBrowsing, slug])

    const handleOpenRoom = useCallback(
        (slug: string, options: object = {}) => {
            openRoom(slug, {
                ...options,
                transitionSource: TransitionTypes.MENU,
            })
            // closeDialog(true)
        },
        [isActiveRoomLounge, isRoomPinned],
    )

    const onOpenRoom = useCallback(() => {
        // onCloseDialog()
        // don't resubscribe if the room is already open
        openRoom(!isActiveRoomLounge ? id : roomID, {
            transitionSource: TransitionTypes.MENU,
        })
    }, [id, isActiveRoomLounge, isRoomPinned, roomID])

    const onClickVideo = useCallback(
        (e: Event) => {
            if (alreadyClicked.current) return
            alreadyClicked.current = true
            e.stopPropagation()
            e.preventDefault()
            if (sameModel && room) {
                // same model, no need to navigate away
                onCloseDialog()
            } else {
                if (isActiveRoomLounge) {
                    onCloseDialog()
                    setLoungeSurrogate(id, roomID)
                } else {
                    onOpenRoom()
                }
            }
        },
        [onOpenRoom, sameModel, room, roomID, id],
    )

    const onFriend = useCallback(() => {
        if (!isAuthenticated) {
            closeDialog()
            openAuthenticationDrawer()
            return
        }
        if (isFriend) removeFriend(slug)
        else addFriend(slug)
    }, [isAuthenticated, isFriend, slug])

    const onOpenMessage = useCallback(() => {
        if (!isAuthenticated) {
            closeDialog()
            openAuthenticationDrawer()
            return
        }
        // onCloseDialog()
        minimizeFloater(id, QUICK_REPLY, true)
        openMessage(slug, TransitionTypes.MENU)
    }, [isAuthenticated, id, slug, isActiveRoomLounge])
    const onOpenQuickReply = useCallback(() => {
        if (!isAuthenticated) {
            closeDialog()
            openAuthenticationDrawer()
            return
        }
        onCloseDialog()
        openQuickReply(slug)
    }, [isAuthenticated, id, slug, isActiveRoomLounge])

    const onIgnore = useCallback(() => {
        if (!isAuthenticated) {
            closeDialog()
            openAuthenticationDrawer()
            return
        }
        ignoreUser(slug)
    }, [isAuthenticated, slug])

    const onReportMenu = useCallback(() => {
        if (!isAuthenticated) {
            closeDialog()
            openAuthenticationDrawer()
            return
        }
        onCloseDialog()
        if (!id || !name) return
        openReportUserDrawer({ id, name })
    }, [isAuthenticated, id, name])

    const onHide = useCallback(() => {
        if (!isAuthenticated) {
            closeDialog()
            openAuthenticationDrawer()
            return
        }
        hideModel(slug)
        closeDialog(true)
        if (isHidden) return
        openHideModelConfirmation()
    }, [isAuthenticated, slug, isHidden])

    const onBookmark = useCallback(() => {
        if (!isAuthenticated) {
            closeDialog()
            openAuthenticationDrawer()
            return
        }
        if (isBookmark) removeBookmark(slug)
        else bookmarkModel(slug)
    }, [isAuthenticated, isBookmark, slug])

    const onOpenProfile = useCallback(() => {
        onCloseDialog()
        openProfile(slug)
    }, [name, slug, inRoomOrMessage, isAuthenticated])

    const onOpenWhiteboard = useCallback(() => {
        openWhiteboardDialog(slug)
    }, [enableBrowsing, slug])

    const onOpenTip = useCallback(() => {
        onCloseDialog()
        openTipsDrawer({ slug })
    }, [slug])

    const openPrevious = useCallback(() => {
        const previous = getNextOrPreviousModel(slug, {
            previous: true,
            returnSlug: 1,
        })
        openMenuDialog(previous)
    }, [slug])

    const openNext = useCallback(() => {
        const next = getNextOrPreviousModel(slug, {
            returnSlug: 1,
        })
        openMenuDialog(next)
    }, [slug])

    const onPin = useCallback(() => {
        motionTransition(
            () =>
                pinRoom(id, {
                    pinned: !isRoomPinned,
                    skipClose: sameModel,
                }),
            {
                id,
                skipTransition: isRoomPinned,
                transitionSource: TransitionTypes.MENU,
                transitionDestination: TransitionTypes.PINROOM,
            },
        )
    }, [id, isRoomPinned, sameModel, showSinglePin])

    const browsingButtons = enableBrowsing && (
        <Fragment>
            <IconButton
                data-testid="IconButton__Previous"
                onClick={openPrevious}
                css={[navButtonStyles, previousButtonStyles]}
            >
                <PreviousIcon />
            </IconButton>
            <IconButton
                data-testid="IconButton__Next"
                onClick={openNext}
                css={[navButtonStyles, nextButtonStyles]}
            >
                <NextIcon />
            </IconButton>
        </Fragment>
    )

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
        modelGetSocialMedia(slug)
    }, [slug])

    if (!menuOpen) {
        return null
    }

    if (!slug || !name) {
        return (
            <Fragment>
                <StyledRoot>
                    <Empty
                        before={i18n.t('menu.userNotFound')}
                        after={i18n.t('menu.viewableMenu')}
                    />
                </StyledRoot>
                {browsingButtons}
            </Fragment>
        )
    }

    if (!isModel) {
        return (
            <StyledRoot id="menu">
                <FlexGrid overrides={memberMenuStyles}>
                    <Container maxWidth="xs">
                        <NameAvatar
                            addBoxShadow={true}
                            offline={isOffline}
                            name={name}
                            id={id}
                        />
                        <FlexGrid>
                            <Actions
                                onFriend={onFriend}
                                onIgnore={onIgnore}
                                onOpenMessage={onOpenMessage}
                                onReportMenu={onReportMenu}
                                isFriend={isFriend}
                                isIgnored={isIgnored}
                                hasNote={hasNote}
                                isHidden={isHidden}
                                isBookmark={isBookmark}
                                hasWhiteboard={hasWhiteboard}
                                isPublicShow={isPublicShow}
                                isOffline={isOffline}
                                isModel={false}
                                header={false}
                                slug={slug}
                                name={name}
                                id={id}
                                reportMenu
                            />
                        </FlexGrid>
                    </Container>
                </FlexGrid>
            </StyledRoot>
        )
    }

    if (banned || isKicked) {
        return (
            <Fragment>
                <StyledRoot>
                    <Empty
                        before={i18n.t(banned ? 'menu.banned' : 'menu.kicked')}
                        after={i18n.t('menu.viewableMenu')}
                    />
                </StyledRoot>
                {browsingButtons}
            </Fragment>
        )
    }

    return (
        <Fragment>
            <StyledRoot id="menu">
                {isBroadcasting && !hiddenShow && (
                    <StyledVideoContainer
                        data-testid="LiveSnapStream__VideoPlayer"
                        css={streamStyles}
                    >
                        <Video
                            onclick={onClickVideo}
                            styles="cursor: pointer; border-top-left-radius: 4px; border-top-right-radius: 4px;"
                            videoType={VideoType.Menu}
                            onlyVolumeControls
                            forceHeight
                            forefront
                            autoMute
                            id={id}
                        />
                    </StyledVideoContainer>
                )}
                <StyledBody className="no-scrollbars" ref={scrollRef}>
                    {(!isBroadcasting || hiddenShow) && (
                        <Header
                            onClickPreview={onOpenRoom}
                            broadcasting={isBroadcasting}
                            showViewers={true}
                            offline={isOffline}
                            viewers={viewers}
                            hasShare={hasShare}
                            is16x9={is16x9}
                            color={modelColor}
                            slug={slug}
                            name={name}
                            rank={rank}
                            id={id}
                        />
                    )}
                    {isBroadcasting && !hiddenShow && (
                        <StyledMenuHeaderGrid data-testid="Grid__MenuHeader">
                            {hasShare && (
                                <div
                                    css={shareLogoStyles}
                                    data-testid="Grid__Share"
                                >
                                    <Share slug={slug} width={80} />
                                </div>
                            )}
                            <div css={avatarStyles}>
                                <BadgeAvatar
                                    broadcasting={isBroadcasting}
                                    offline={isOffline}
                                    size={isSmallResolution ? 40 : 60}
                                    addBoxShadow
                                    disableDrag
                                    alt={name}
                                    id={id}
                                />
                            </div>
                            <StyledNameTopicGrid>
                                <div data-testid="Menu__Name">
                                    <Name color={modelColor} text={name} />
                                </div>
                                {(typeof viewers === 'number' ||
                                    !!age ||
                                    !!country) && (
                                    <StyledModelDetailsRow data-testid="Menu__ViewersAgeLocation">
                                        {typeof viewers === 'number' && (
                                            <CounterViewers
                                                size={0.6}
                                                styleOverrides={
                                                    counterViewerStyles
                                                }
                                                viewers={viewers}
                                            />
                                        )}
                                        {!!age && (
                                            <StyledModelDetailElement data-testid="Menu__ViewersAge">
                                                <Calendar size={0.5} />
                                                {age}
                                            </StyledModelDetailElement>
                                        )}
                                        {!!country && (
                                            <StyledModelDetailElement
                                                css={{ overflow: 'hidden' }}
                                                data-testid="Menu__ViewersLocation"
                                            >
                                                <MapMarker size={0.5} />
                                                <StyledCountryLabel>
                                                    {country}
                                                </StyledCountryLabel>
                                            </StyledModelDetailElement>
                                        )}
                                    </StyledModelDetailsRow>
                                )}
                                <div
                                    data-testid="Menu__Topic"
                                    css={topicGridStyles}
                                    style={{ color: modelColor }}
                                >
                                    {topic}
                                </div>
                            </StyledNameTopicGrid>
                        </StyledMenuHeaderGrid>
                    )}
                    {showScorePanel && (
                        <FlexGrid direction="row" overrides={rankStyles}>
                            {!hideCamscore && score && (
                                <StyledRank data-testid="Menu__Camscore">
                                    <strong>{i18n.t('menu.camscore')}</strong>:{' '}
                                    {score}
                                </StyledRank>
                            )}
                            {rank > 0 && (
                                <StyledRank data-testid="Menu__MissMFCRank">
                                    <strong>{i18n.t('menu.missMFC')}: #</strong>
                                    {rank}
                                </StyledRank>
                            )}
                        </FlexGrid>
                    )}
                    <Actions
                        onBookmark={onBookmark}
                        onFriend={onFriend}
                        onHide={onHide}
                        onIgnore={onIgnore}
                        onOpenMessage={onOpenMessage}
                        onOpenQuickReply={onOpenQuickReply}
                        onOpenProfile={onOpenProfile}
                        onOpenRoom={handleOpenRoom}
                        onOpenTip={onOpenTip}
                        onOpenWhiteboard={onOpenWhiteboard}
                        onPin={onPin}
                        onReportMenu={onReportMenu}
                        isFriend={isFriend}
                        isIgnored={isIgnored}
                        hasNote={hasNote}
                        isHidden={isHidden}
                        isBookmark={isBookmark}
                        hasWhiteboard={hasWhiteboard}
                        isPublicShow={isPublicShow}
                        isOffline={isOffline}
                        isModel={true}
                        isRoomPinned={isRoomPinned}
                        canPin={canPin}
                        showQuickReply={isFriend || isBookmark}
                        message={message}
                        profile={slug}
                        slug={slug}
                        name={name}
                        id={id}
                        reportMenu
                    />
                    <StyledIconsContainer>
                        {!!instagram && (
                            <StyledSocialIconWrapper>
                                <SocialIcon
                                    url={`https://www.instagram.com/${instagram}`}
                                    platform={SocialPlatforms.instagram}
                                />
                            </StyledSocialIconWrapper>
                        )}
                        {!!twitter && (
                            <StyledSocialIconWrapper>
                                <SocialIcon
                                    url={`https://twitter.com/${twitter}`}
                                    platform={SocialPlatforms.twitter}
                                />
                            </StyledSocialIconWrapper>
                        )}
                        {hasShare && (
                            <Share
                                albums={albums}
                                recordings={recordings}
                                width={100}
                                slug={slug}
                            />
                        )}
                    </StyledIconsContainer>
                    {!!tags && (
                        <Tags
                            onClick={onCloseDialog}
                            styleOverrides={tagsStyles}
                            tags={!!tags && tags.split('  ')}
                        />
                    )}
                    <div
                        // onClick={onCloseDialog}
                        css={{
                            paddingTop: 4,
                        }}
                    >
                        <YouMayLike id={id} hideNoResultNotice={true} />
                    </div>
                </StyledBody>
            </StyledRoot>
            {browsingButtons}
        </Fragment>
    )
})

export default Menu
